import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useFeaturesStore } from '../stores';
import { API_URL, formatCurrency, getCategoryColor } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, Package, Edit2, Trash2, Speaker, UtensilsCrossed,
  Sofa, Lightbulb, Tent, X, LayoutList, LayoutGrid,
} from 'lucide-react';

const CATEGORY_ICONS = {
  audio: Speaker, catering: UtensilsCrossed, furniture: Sofa,
  lighting: Lightbulb, tent: Tent, other: Package,
};

// ============= LIST VIEW CARD =============
const ProductCard = ({ product, onEdit, onDelete, catLabel }) => {
  const Icon = CATEGORY_ICONS[product.category] || Package;
  const attributes = product.attributes || {};
  return (
    <Card className="card-hover group" data-testid={`product-card-${product.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl ${getCategoryColor(product.category)} flex items-center justify-center shrink-0`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-medium truncate">{product.name}</h3>
                <p className="text-sm text-muted-foreground">{product.description || 'Aucune description'}</p>
              </div>
              <p className="text-lg font-bold whitespace-nowrap">{formatCurrency(product.price_ht)}</p>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <Badge variant="outline" className={getCategoryColor(product.category)}>
                {catLabel}
              </Badge>
              <Badge variant="outline" className="bg-secondary">Stock: {product.stock_quantity}</Badge>
              {Object.entries(attributes).slice(0, 3).map(([key, value]) => (
                <Badge key={key} variant="outline" className="bg-primary/10 text-primary border-primary/20">
                  {typeof value === 'boolean' ? (value ? key : '') : `${key}: ${value}`}
                </Badge>
              ))}
            </div>
            <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="outline" size="sm" onClick={() => onEdit(product)} data-testid={`edit-product-${product.id}`}>
                <Edit2 className="w-4 h-4 mr-1" /> Modifier
              </Button>
              <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => onDelete(product.id)} data-testid={`delete-product-${product.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============= WAREHOUSE GRID CARD =============
const WarehouseCard = ({ product, onEdit, onDelete, catLabel }) => {
  const Icon = CATEGORY_ICONS[product.category] || Package;
  const stock = product.stock_quantity || 0;
  const maxStock = Math.max(stock, 1);
  const percent = Math.min(100, (stock / maxStock) * 100);
  const stockColor = stock === 0 ? 'bg-red-500' : stock <= 3 ? 'bg-orange-500' : 'bg-emerald-500';
  const stockTextColor = stock === 0 ? 'text-red-500' : stock <= 3 ? 'text-orange-500' : 'text-emerald-500';

  return (
    <Card className="group hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer" data-testid={`warehouse-card-${product.id}`} onClick={() => onEdit(product)}>
      <CardContent className="p-0">
        {/* Visual header with icon */}
        <div className={`h-28 rounded-t-lg ${getCategoryColor(product.category)} flex items-center justify-center relative overflow-hidden`}>
          <Icon className="w-12 h-12 opacity-80" />
          {product.image_url && (
            <img src={product.image_url} alt={product.name} className="absolute inset-0 w-full h-full object-cover" />
          )}
          <Badge className="absolute top-2 right-2 text-[10px]" variant="secondary">
            {catLabel}
          </Badge>
        </div>

        <div className="p-3 space-y-2">
          <h3 className="font-medium text-sm truncate">{product.name}</h3>
          <p className="text-lg font-heading font-bold">{formatCurrency(product.price_ht)}</p>

          {/* Stock Gauge */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Stock</span>
              <span className={`text-xs font-medium ${stockTextColor}`}>
                {stock} disponible{stock !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${stockColor}`} style={{ width: `${percent}%` }} />
            </div>
          </div>

          {/* Actions on hover */}
          <div className="flex gap-1 pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="outline" size="sm" className="flex-1 h-7 text-xs" onClick={(e) => { e.stopPropagation(); onEdit(product); }}>
              <Edit2 className="w-3 h-3 mr-1" /> Modifier
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs text-destructive" onClick={(e) => { e.stopPropagation(); onDelete(product.id); }}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ============= ATTRIBUTES EDITOR =============
const AttributesEditor = ({ attributes, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const addAttribute = () => {
    if (newKey.trim()) {
      onChange({ ...attributes, [newKey.trim()]: newValue.trim() });
      setNewKey(''); setNewValue('');
    }
  };
  const removeAttribute = (key) => { const a = { ...attributes }; delete a[key]; onChange(a); };
  return (
    <div className="space-y-3">
      <Label>Attributs personnalisés</Label>
      <div className="flex flex-wrap gap-2">
        {Object.entries(attributes).map(([key, value]) => (
          <Badge key={key} variant="secondary" className="gap-1 py-1.5">
            <span className="font-medium">{key}:</span> {String(value)}
            <button type="button" onClick={() => removeAttribute(key)} className="ml-1 hover:text-destructive"><X className="w-3 h-3" /></button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input placeholder="Clé" value={newKey} onChange={(e) => setNewKey(e.target.value)} className="flex-1" />
        <Input placeholder="Valeur" value={newValue} onChange={(e) => setNewValue(e.target.value)} className="flex-1" />
        <Button type="button" variant="outline" onClick={addAttribute}><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

// ============= PRODUCT FORM =============
const ProductFormDialog = ({ product, open, onOpenChange, onSave, categories }) => {
  const [formData, setFormData] = useState(product || { name: '', description: '', price_ht: '', tax_rate: 20, stock_quantity: 0, category: 'other', unit: 'unité', attributes: {} });
  React.useEffect(() => {
    setFormData(product || { name: '', description: '', price_ht: '', tax_rate: 20, stock_quantity: 0, category: 'other', unit: 'unité', attributes: {} });
  }, [product, open]);
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, price_ht: parseFloat(formData.price_ht) || 0, stock_quantity: parseInt(formData.stock_quantity) || 0, tax_rate: parseFloat(formData.tax_rate) || 20 });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{product ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2"><Label>Nom *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="product-name-input" /></div>
            <div className="space-y-2"><Label>Catégorie</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(categories || []).map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Unité</Label>
              <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unité">Unité</SelectItem><SelectItem value="mètre">Mètre</SelectItem>
                  <SelectItem value="kg">Kilogramme</SelectItem><SelectItem value="heure">Heure</SelectItem><SelectItem value="jour">Jour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Prix HT (€) *</Label><Input type="number" step="0.01" min="0" value={formData.price_ht} onChange={(e) => setFormData({ ...formData, price_ht: e.target.value })} required data-testid="product-price-input" /></div>
            <div className="space-y-2"><Label>Stock</Label><Input type="number" min="0" value={formData.stock_quantity} onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })} data-testid="product-stock-input" /></div>
            <div className="col-span-2 space-y-2"><Label>Description</Label><Textarea value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} /></div>
            <div className="col-span-2"><AttributesEditor attributes={formData.attributes || {}} onChange={(attrs) => setFormData({ ...formData, attributes: attrs })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" data-testid="product-submit-btn">{product ? 'Enregistrer' : 'Créer'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============= MAIN PAGE =============
const ProductsPage = () => {
  const { token } = useAuthStore();
  const goldEnabled = useFeaturesStore((s) => s.gold);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/products`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/categories`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const getCatLabel = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/products`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(data) });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); toast.success('Produit créé'); setDialogOpen(false); },
    onError: () => toast.error('Erreur'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/products/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(data) });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); toast.success('Produit mis à jour'); setDialogOpen(false); setEditingProduct(null); },
    onError: () => toast.error('Erreur'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/products/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }); toast.success('Produit supprimé'); },
    onError: () => toast.error('Erreur'),
  });

  const handleSave = (data) => { editingProduct ? updateMutation.mutate({ id: editingProduct.id, data }) : createMutation.mutate(data); };
  const handleEdit = (p) => { setEditingProduct(p); setDialogOpen(true); };
  const handleDelete = (id) => { if (window.confirm('Supprimer ce produit ?')) deleteMutation.mutate(id); };

  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) || (p.description || '').toLowerCase().includes(search.toLowerCase()) || Object.values(p.attributes || {}).some((v) => String(v).toLowerCase().includes(search.toLowerCase()));
    return matchesSearch && (categoryFilter === 'all' || p.category === categoryFilter);
  });

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Produits</h1>
          <p className="text-muted-foreground">Gérez votre catalogue polymorphe</p>
        </div>
        <div className="flex items-center gap-2">
          {goldEnabled && (
            <div className="flex border border-border rounded-lg overflow-hidden" data-testid="view-toggle">
              <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setViewMode('list')} data-testid="view-list-btn">
                <LayoutList className="w-4 h-4" />
              </Button>
              <Button variant={viewMode === 'warehouse' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setViewMode('warehouse')} data-testid="view-warehouse-btn">
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
          )}
          <Button onClick={() => { setEditingProduct(null); setDialogOpen(true); }} data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouveau produit
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Rechercher un produit..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="product-search" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="category-filter"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className={viewMode === 'warehouse' && goldEnabled ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4' : 'space-y-3'}>
          {[...Array(4)].map((_, i) => <Card key={i}><CardContent className="p-4"><Skeleton className="h-24 w-full" /></CardContent></Card>)}
        </div>
      ) : filteredProducts.length > 0 ? (
        viewMode === 'warehouse' && goldEnabled ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="warehouse-grid">
            {filteredProducts.map((p) => <WarehouseCard key={p.id} product={p} onEdit={handleEdit} onDelete={handleDelete} catLabel={getCatLabel(p.category)} />)}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProducts.map((p) => <ProductCard key={p.id} product={p} onEdit={handleEdit} onDelete={handleDelete} catLabel={getCatLabel(p.category)} />)}
          </div>
        )
      ) : (
        <Card><CardContent className="p-12 text-center">
          <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading font-semibold mb-2">Aucun produit trouvé</h3>
          <Button onClick={() => { setEditingProduct(null); setDialogOpen(true); }}><Plus className="w-4 h-4 mr-2" /> Créer un produit</Button>
        </CardContent></Card>
      )}

      <ProductFormDialog product={editingProduct} open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingProduct(null); }} onSave={handleSave} categories={categories} />
    </div>
  );
};

export default ProductsPage;
